import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
  CSSProperties,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import styles from './floating-actions-dropdown.module.scss';

export interface FloatingActionsDropdownRef {
  /** Closes the dropdown */
  close: () => void;
  /** Opens the dropdown */
  open: () => void;
  /** Toggles the dropdown open/closed state */
  toggle: () => void;
}

export interface FloatingActionsDropdownProps {
  /** The alignment of the dropdown */
  alignment?: 'left' | 'right';
  /** The content rendered inside the dropdown */
  content?: React.ReactNode;
  /** The direction of the dropdown */
  direction?: 'up' | 'down';
  /** Indicates whether the option is disabled */
  disabled?: boolean;
  /** The unique id of the modal */
  modalId?: string;
  /** Function to call on dropdown state change */
  onDropdownToggle?: (isOpen: boolean) => void;
  /** The content rendered inside the trigger button */
  trigger?: React.ReactNode;
}

const FloatingActionsDropdown = forwardRef<
  FloatingActionsDropdownRef,
  FloatingActionsDropdownProps
>(
  (
    {
      alignment = 'left',
      content,
      direction = 'down',
      disabled = false,
      modalId,
      onDropdownToggle,
      trigger,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    const placement =
      direction === 'up'
        ? alignment === 'right'
          ? 'top-end'
          : 'top-start'
        : alignment === 'right'
          ? 'bottom-end'
          : 'bottom-start';

    const { refs, floatingStyles, context } = useFloating({
      open: isOpen,
      onOpenChange: setIsOpen,
      placement,
      whileElementsMounted: autoUpdate,
      middleware: [offset(8), flip(), shift({ padding: 8 })],
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);

    const { getReferenceProps, getFloatingProps } = useInteractions([
      click,
      dismiss,
    ]);

    const style = {
      '--actions-dropdown-cursor': disabled ? 'default' : 'pointer',
    } as CSSProperties;

    useImperativeHandle(ref, () => ({
      toggle: () => {
        setIsOpen(prev => !prev);
      },
      open: () => {
        setIsOpen(true);
      },
      close: () => {
        setIsOpen(false);
      },
    }));

    useEffect(() => {
      onDropdownToggle?.(isOpen);
    }, [isOpen, onDropdownToggle]);

    return (
      <div
        className={classNames(styles.component, {
          [styles.isDisabled]: disabled,
        })}
        style={style}
      >
        <button
          ref={refs.setReference}
          disabled={disabled}
          {...getReferenceProps()}
        >
          {trigger}
        </button>

        {/* Use a portal to prevent the dropdown from being clipped by parent containers
        (e.g. `overflow: hidden`). Floating UI handles calculating and updating the
        dropdown position relative to its trigger element. */}

        <FloatingPortal id={modalId}>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                ref={refs.setFloating}
                style={floatingStyles}
                {...getFloatingProps()}
              >
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: direction === 'down' ? -8 : 8 }}
                  initial={{ opacity: 0, y: direction === 'down' ? -8 : 8 }}
                  transition={{ duration: 0.2 }}
                >
                  {content}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </FloatingPortal>
      </div>
    );
  },
);

FloatingActionsDropdown.displayName = 'FloatingActionsDropdown';

export default FloatingActionsDropdown;
